import { trackPage } from "@/lib/analytics";
import { splitLocale } from "@/lib/i18n/paths";

// Secciones del sitio para las métricas internas (/admin/estadisticas). Misma
// lista que el server (site-section-daily.schema.ts).
export type SiteSection =
  | "home"
  | "events"
  | "event"
  | "cinema"
  | "games"
  | "organizations"
  | "panoramas"
  | "auth"
  | "dashboard"
  | "other";

// Rutas del panel de cada cuenta (crear/editar eventos, perfil, etc.).
const DASHBOARD = /^\/(dashboard|mis-[^/]+|interes|perfil|notificaciones|integraciones|organizacion)(\/|$)|^\/eventos\/(nuevo|carga-masiva)(\/|$)|^\/eventos\/[^/]+\/editar(\/|$)/;
const AUTH = /^\/(login|registro|forgot-password|reset-password)(\/|$)/;

// null = no se mide (el panel de administración).
export function sectionForPath(pathname: string): SiteSection | null {
  const path = splitLocale(pathname).path.replace(/\/+$/, "") || "/";
  if (path === "/") return "home";
  if (/^\/admin(\/|$)/.test(path)) return null;
  if (DASHBOARD.test(path)) return "dashboard";
  if (AUTH.test(path)) return "auth";
  if (/^\/evento\//.test(path)) return "event";
  if (/^\/eventos(\/|$)/.test(path)) return "events";
  if (/^\/viva-cine(\/|$)/.test(path)) return "cinema";
  if (/^\/juegos(\/|$)/.test(path)) return "games";
  if (/^\/organizaciones(\/|$)/.test(path)) return "organizations";
  if (/^\/panorama(\/|$)/.test(path)) return "panoramas";
  return "other";
}

// Origen de un paso entre secciones: 'entry' = primera página de la visita.
export type TransitionSource = SiteSection | "entry";

// Recorrido de esta pestaña: secciones visitadas, las que ya se acreditaron
// al embudo (para no contarlas dos veces si se abren varios eventos) y la
// última sección (para contar el paso desde→hasta). Vive
// en sessionStorage y se reinicia tras 30 min sin actividad, igual que una
// visita.
const STATE_KEY = "cv_sections";
const SESSION_IDLE_MS = 30 * 60 * 1000;

interface TabState {
  visited: SiteSection[];
  credited: SiteSection[];
  last?: SiteSection;
  at: number;
}

function readState(): TabState {
  try {
    const raw = sessionStorage.getItem(STATE_KEY);
    const state = raw ? (JSON.parse(raw) as TabState) : null;
    if (state && Date.now() - state.at < SESSION_IDLE_MS) return state;
  } catch {
    // Almacenamiento bloqueado o dato corrupto: se parte de cero.
  }
  return { visited: [], credited: [], at: Date.now() };
}

function writeState(state: TabState): void {
  try {
    sessionStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {}
}

// Llamar en cada cambio de ruta. Al abrir una ficha de evento se mandan las
// secciones recorridas antes que todavía no llevaban a un evento.
export function recordSectionView(pathname: string): void {
  const section = sectionForPath(pathname);
  if (!section) return;
  const state = readState();
  const newSession = !state.visited.includes(section);
  let toEventFrom: SiteSection[] | undefined;
  if (section === "event") {
    toEventFrom = state.visited.filter((s) => s !== "event" && !state.credited.includes(s));
    state.credited.push(...toEventFrom);
  }
  // Seguir dentro de la misma sección (p. ej. cambiar filtros) no es un paso.
  const from: TransitionSource | undefined = state.last === section ? undefined : (state.last ?? "entry");
  if (newSession) state.visited.push(section);
  state.last = section;
  state.at = Date.now();
  writeState(state);
  trackPage(section, newSession, toEventFrom, from);
}
